import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Pressable, StyleSheet, Platform, View, ActionSheetIOS, Alert, Linking } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import Icon from '@/components/Icon';
import { useFileStore, useToastStore } from '@/store';
import { MAX_FILES_UPLOADED_IN_MESSAGE } from '@/constants/config';
import { uploadFilesWithPlaceholders } from '@/lib/uploadFileWithPlaceholder';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import useRequireAuth from '@/hooks/useRequireAuth';

const createStyles = (colors) => StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: 'transparent',
    borderRadius: 17,
    height: 34,
    minWidth: 34,
    paddingHorizontal: 8,
    ...Platform.select({
      web: {
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      }
    })
  },
  buttonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  hiddenInput: {
    display: 'none',
  },
});

export default function ChatInputMobileFileUploadButton({ 
  files = [], 
  onAddFile, 
  onAddUploadingFile,
  collectionId = null,
  isDisabled = false,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef(null);
  
  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } = useFileStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const requireAuth = useRequireAuth();

  const hasReachedFileLimit = files?.length >= MAX_FILES_UPLOADED_IN_MESSAGE;

  // Shared function to process and upload picked files
  const processFiles = useCallback(async (pickedFiles) => {
    await uploadFilesWithPlaceholders(pickedFiles, {
      collectionId,
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      t,
    });
  }, [isFileAllowed, uploadFile, addToast, onAddFile, onAddUploadingFile, collectionId, t]);

  // Pick from photo library
  const handlePickFromPhotos = useCallback(async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images', 'videos'],
        allowsMultipleSelection: true,
        quality: 0.8,
      });

      if (result.canceled) return;

      const files = result.assets.map(asset => ({
        uri: asset.uri,
        name: asset.fileName || asset.uri.split('/').pop() || 'photo.jpg',
        size: asset.fileSize || 0,
        type: asset.mimeType || (asset.type === 'video' ? 'video/mp4' : 'image/jpeg'),
      }));

      await processFiles(files);
    } catch (error) {
      console.error('Error picking from photos:', error);
      addToast(t('chatInput.files.pickFromPhotosFailed'), 'error');
    }
  }, [addToast, processFiles]);

  // Take a photo with camera
  const handleTakePhoto = useCallback(async () => {
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (permission.status !== 'granted') {
        if (permission.canAskAgain === false) {
          addToast(t('chatInput.files.cameraDisabled'), 'error');
          await Linking.openSettings().catch((error) => {
            console.warn('[FileUpload] Failed to open settings:', error);
          });
          return;
        }
        addToast(t('chatInput.files.cameraPermission'), 'error');
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        quality: 0.8,
      });

      if (result.canceled) return;

      const asset = result.assets[0];
      const file = {
        uri: asset.uri,
        name: asset.fileName || `camera_photo_${Date.now()}.jpg`,
        size: asset.fileSize || 0,
        type: asset.mimeType || 'image/jpeg',
      };

      await processFiles([file]);
    } catch (error) {
      console.error('Error taking photo:', error);
      addToast(t('chatInput.files.takePhotoFailed'), 'error');
    }
  }, [addToast, processFiles]);

  // Pick from files (document picker)
  const handlePickFromFiles = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: true,
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const files = result.assets.map(asset => ({
        uri: asset.uri,
        name: asset.name,
        size: asset.size,
        type: asset.mimeType,
      }));

      await processFiles(files);
    } catch (error) {
      console.error('Error picking document:', error);
      addToast(t('chatInput.files.pickDocumentFailed'), 'error');
    }
  }, [addToast, processFiles]);

  const handlePress = useCallback(async () => {
    // Gate before any picker invocation so logged-out visitors see the auth
    // CTA instead of an OS permission prompt for an upload that would fail.
    const gated = requireAuth(() => true, {
      source: 'composer_mobile_upload',
      title: t('chatInput.authCta.uploadTitle'),
      subtitle: t('chatInput.authCta.uploadSubtitle'),
      icon: 'paperclip',
    });
    if (gated !== true) return;

    if (hasReachedFileLimit) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }

    // Ensure allowed file types are loaded
    try { 
      await fetchAllowedFileTypes(); 
    } catch (err) {
      console.error('[FileUpload] Failed to fetch allowed file types:', err);
    }

    // Check if user has credits before processing any files
    const { hasCredits, message: creditsMessage } = hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      return;
    }

    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: [t('chatInput.files.actionSheetCancel'), t('chatInput.files.actionSheetTakePhoto'), t('chatInput.files.actionSheetChooseFromLibrary'), t('chatInput.files.actionSheetBrowseFiles')],
          cancelButtonIndex: 0,
        },
        (buttonIndex) => {
          switch (buttonIndex) {
            case 1:
              handleTakePhoto();
              break;
            case 2:
              handlePickFromPhotos();
              break;
            case 3:
              handlePickFromFiles();
              break;
          }
        }
      );
    } else {
      // Android: same three-way choice iOS gets via ActionSheet. RN Alert is
      // capped at three buttons on Android; dismissing outside cancels.
      Alert.alert(
        t('chatInput.files.actionSheetTitle') || 'Add to your message',
        undefined,
        [
          { text: t('chatInput.files.actionSheetTakePhoto'), onPress: handleTakePhoto },
          { text: t('chatInput.files.actionSheetChooseFromLibrary'), onPress: handlePickFromPhotos },
          { text: t('chatInput.files.actionSheetBrowseFiles'), onPress: handlePickFromFiles },
        ],
        { cancelable: true },
      );
    }
  }, [hasReachedFileLimit, addToast, fetchAllowedFileTypes, hasCreditsForFileUpload, handleTakePhoto, handlePickFromPhotos, handlePickFromFiles, requireAuth, t]);

  const handleFileChange = useCallback(async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    // Check if user has credits before processing any files
    const { hasCredits, message: creditsMessage } = hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      // Reset the input value so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    await uploadFilesWithPlaceholders(selectedFiles, {
      collectionId,
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      t,
      extraTempFields: (file) => ({ previewFile: file }),
    });
    
    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isFileAllowed, uploadFile, addToast, onAddFile, onAddUploadingFile, hasCreditsForFileUpload, collectionId, t]);

  const buttonDisabled = isDisabled || hasReachedFileLimit;

  return (
    <>
      <Pressable
        style={[
          styles.button, 
          isHovered && styles.buttonHovered,
          buttonDisabled && styles.buttonDisabled,
        ]}
        onPress={handlePress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        disabled={buttonDisabled}
        accessibilityLabel={t('chatInput.files.attachFile')}
        accessibilityRole="button"
        accessibilityHint={Platform.OS === 'macos' ? 'Opens file browser to select files' : 'Opens options to take photo, choose from library, or browse files'}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <View style={styles.inner}>
          <Icon name="paperclip" size={14} color={colors.text_color_primary} />
        </View>
      </Pressable>
      {Platform.OS === 'web' && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={styles.hiddenInput}
          multiple
        />
      )}
    </>
  );
}
